import React, { useState, useEffect, useMemo } from 'react'

import {
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormLabel,
    IconButton,
    InputAdornment,
    Card,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Delete as DeleteIcon, Add as AddIcon } from '@material-ui/icons'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'

import {
    GUEST_CARD_FIELD_QUESTIONS,
    GuestCardQuestionFieldKey,
    isGuestCardFieldLocked,
    isGuestCardFieldRequired,
    guestCardDefaultMoveInDate,
    guestCardDefaultMoveOutDate,
} from '../../../../models/Outreach/services'
import {
    GuestCardFormResponse,
    GuestCardFormRequest,
} from '../../../../hooks/Outreach/useGuestCardForm'
import { useUser } from '../../../../hooks/useUser'
import { useServedSchools } from '../../../../hooks/useServedSchools'
import { School, schoolDisplayName } from '../../../../models/types'
import { Prospect } from '../../../../models/Outreach/types'
import { GuestCardPremiumQuestion } from '../../Campaign/GuestCards/GuestCardPremiumQuestion'
import { GuestCardFieldEditorChrome } from '../../Campaign/GuestCards/GuestCardFieldEditorChrome'

export interface GuestCardFormEditorHandlers {
    fieldRequired: (field: GuestCardQuestionFieldKey) => boolean
    onRemoveField: (field: GuestCardQuestionFieldKey) => void
    onToggleRequired: (
        field: GuestCardQuestionFieldKey,
        required: boolean,
    ) => void
    onRemoveCustomQuestion: (questionId: number) => void
    onToggleCustomRequired: (questionId: number, required: boolean) => void
}
import { CustomQuestionField } from '../../Campaign/GuestCards/CustomQuestionField'

interface GuestCardFormFieldsProps {
    guestCardFormData: GuestCardFormResponse
    prospect: Prospect | null
    onSubmit: (formData: GuestCardFormRequest) => void
    isLoading?: boolean
    submitLabel?: string
    loadingLabel?: string
    hideSubmit?: boolean
    editor?: GuestCardFormEditorHandlers
}

const useStyles = makeStyles((theme: Theme) => ({
    formField: {
        marginBottom: theme.spacing(2.5),
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
        },
    },
    sectionTitle: {
        fontSize: '1rem',
        fontWeight: 600,
        color: '#374151',
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1.5),
        paddingBottom: theme.spacing(1),
        borderBottom: '1px solid #e5e7eb',
    },
    submitButton: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(1.5),
        borderRadius: '8px',
        textTransform: 'none',
        fontSize: '1rem',
        fontWeight: 600,
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        '&:hover': {
            background: 'linear-gradient(135deg, #2563eb, #1e40af)',
        },
    },
}))

export const GuestCardFormFields: React.FC<GuestCardFormFieldsProps> = ({
    guestCardFormData,
    prospect,
    onSubmit,
    isLoading = false,
    submitLabel = 'Convert to Lead',
    loadingLabel = 'Converting...',
    hideSubmit = false,
    editor,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const { workspaceUser } = useUser()
    const apartmentId =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'
            ? workspaceUser.active_workspace.id
            : undefined
    const payloadSchools = guestCardFormData?.schools
    const { servedSchools, isLoading: servedSchoolsLoading } = useServedSchools(
        payloadSchools != null ? undefined : apartmentId,
    )
    const schoolList = useMemo(() => {
        if (payloadSchools != null) return payloadSchools
        return (servedSchools?.schools || [])
            .filter((row) => row.is_served)
            .map((row) => ({
                id: row.school_id,
                name: row.effective_display_name,
                effective_display_name: row.effective_display_name,
            })) as School[]
    }, [payloadSchools, servedSchools])
    const schoolListLoading =
        payloadSchools != null ? false : servedSchoolsLoading

    // Form state
    const [formData, setFormData] = useState<Record<string, string>>({})
    const [hasRoommates, setHasRoommates] = useState<string>('no')
    const [roommates, setRoommates] = useState<
        Array<{ name: string; email: string; phone: string }>
    >([])
    const [selectedPremiumIds, setSelectedPremiumIds] = useState<number[]>([])
    const [premiumsNone, setPremiumsNone] = useState(false)

    // Phone normalization
    const normalizePhone = (value: string): string => {
        const digits = value.replace(/\D/g, '')
        if (digits.length <= 3) {
            return digits
        } else if (digits.length <= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
        } else {
            return `(${digits.slice(0, 3)}) ${digits.slice(
                3,
                6,
            )}-${digits.slice(6, 10)}`
        }
    }

    // Pre-fill form with prospect data
    useEffect(() => {
        if (guestCardFormData) {
            const initialData: Record<string, string> = {}
            if (guestCardFormData.name)
                initialData.name = guestCardFormData.name
            if (guestCardFormData.email) {
                initialData.email = guestCardFormData.email
            }
            if (guestCardFormData.phone) {
                initialData.phone = normalizePhone(guestCardFormData.phone)
            }
            const moveIn = guestCardDefaultMoveInDate(guestCardFormData)
            if (moveIn) {
                initialData.preferred_move_in_date = moveIn
            }
            const moveOut = guestCardDefaultMoveOutDate(guestCardFormData)
            if (moveOut) {
                initialData.preferred_move_out_date = moveOut
            }
            setFormData(initialData)
        }
    }, [guestCardFormData])

    const guestCard = guestCardFormData?.guest_card

    const isFieldRequired = (field: string) =>
        editor
            ? editor.fieldRequired(field as GuestCardQuestionFieldKey)
            : isGuestCardFieldRequired(guestCard, field)

    const wrapDefault = (
        field: GuestCardQuestionFieldKey,
        node: React.ReactNode,
    ) => {
        if (!editor) {
            return <React.Fragment key={field}>{node}</React.Fragment>
        }
        const locked = isGuestCardFieldLocked(field)
        return (
            <GuestCardFieldEditorChrome
                key={field}
                locked={locked}
                required={isFieldRequired(field)}
                onToggleRequired={
                    locked
                        ? undefined
                        : (required) => editor.onToggleRequired(field, required)
                }
                onRemove={
                    locked ? undefined : () => editor.onRemoveField(field)
                }
            >
                {node}
            </GuestCardFieldEditorChrome>
        )
    }

    const wrapCustom = (questionId: number, node: React.ReactNode) => {
        if (!editor) {
            return (
                <React.Fragment key={`custom-${questionId}`}>
                    {node}
                </React.Fragment>
            )
        }
        const cardQuestion = guestCard?.card_questions.find(
            (item) => item.custom_question.id === questionId,
        )
        return (
            <GuestCardFieldEditorChrome
                key={`custom-${questionId}`}
                required={Boolean(cardQuestion?.required)}
                onToggleRequired={(required) =>
                    editor.onToggleCustomRequired(questionId, required)
                }
                onRemove={() => editor.onRemoveCustomQuestion(questionId)}
            >
                {node}
            </GuestCardFieldEditorChrome>
        )
    }

    const getContactFields = (): Array<
        keyof typeof GUEST_CARD_FIELD_QUESTIONS
    > => {
        if (!guestCard) return []
        const fields: Array<keyof typeof GUEST_CARD_FIELD_QUESTIONS> = []
        if (guestCard.name) fields.push('name')
        if (guestCard.email) fields.push('email')
        if (guestCard.phone) fields.push('phone')
        return fields
    }

    const getAdditionalFields = (): Array<
        keyof typeof GUEST_CARD_FIELD_QUESTIONS
    > => {
        if (!guestCard) return []
        const fields: Array<keyof typeof GUEST_CARD_FIELD_QUESTIONS> = []
        if (guestCard.preferred_unit_config)
            fields.push('preferred_unit_config')
        if (guestCard.preferred_premiums) fields.push('preferred_premiums')
        if (guestCard.preferred_rent_cost) fields.push('preferred_rent_cost')
        if (guestCard.preferred_move_in_date)
            fields.push('preferred_move_in_date')
        if (guestCard.preferred_move_out_date)
            fields.push('preferred_move_out_date')
        if (guestCard.preferred_roommates) fields.push('preferred_roommates')
        if (guestCard.school_attendance) fields.push('school_attendance')
        if (guestCard.additional_requests) fields.push('additional_requests')
        return fields
    }

    const handleFieldChange = (fieldName: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: value,
        }))
    }

    const prepareSubmissionData = (): GuestCardFormRequest | null => {
        if (!guestCard) return null

        const submissionData: GuestCardFormRequest = {}

        const contactFields = getContactFields()
        const additionalFields = getAdditionalFields()

        contactFields.forEach((field) => {
            const fieldName = field as string
            if (fieldName === 'name') {
                submissionData.name = formData[fieldName] || ''
            } else if (fieldName === 'email') {
                submissionData.email = formData[fieldName] || ''
            } else if (fieldName === 'phone') {
                submissionData.phone = formData[fieldName] || ''
            }
        })

        additionalFields.forEach((field) => {
            const fieldName = field as string

            if (fieldName === 'preferred_roommates') {
                const additionalOccupants = roommates
                    .filter((r) => r.name || r.email || r.phone)
                    .map((r) => ({
                        name: r.name || '',
                        email: r.email || '',
                        phone: r.phone || '',
                    }))
                submissionData.additional_occupants = additionalOccupants
            } else if (fieldName === 'school_attendance') {
                const schoolId = formData[fieldName]
                    ? parseInt(formData[fieldName], 10)
                    : null
                submissionData.school = schoolId || undefined
            } else if (fieldName === 'preferred_unit_config') {
                const unitConfigId = formData[fieldName]
                    ? parseInt(formData[fieldName], 10)
                    : null
                submissionData.floor_plan = unitConfigId || undefined
            } else if (fieldName === 'preferred_premiums') {
                submissionData.preferred_premiums = premiumsNone
                    ? []
                    : selectedPremiumIds
            } else if (fieldName === 'preferred_rent_cost') {
                submissionData.preferred_rent_cost = formData[fieldName] || ''
            } else if (
                fieldName === 'preferred_move_in_date' ||
                fieldName === 'preferred_move_out_date'
            ) {
                submissionData[fieldName] = formData[fieldName] || ''
            } else if (fieldName === 'additional_requests') {
                submissionData.additional_requests = formData[fieldName] || ''
            }
        })

        if (guestCard.card_questions && guestCard.card_questions.length > 0) {
            submissionData.custom_questions = guestCard.card_questions.map(
                (cardQuestion) => ({
                    question: cardQuestion.custom_question.id,
                    answer:
                        formData[`custom_${cardQuestion.custom_question.id}`] ||
                        '',
                }),
            )
        }

        return submissionData
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (hideSubmit) return
        const requestBody = prepareSubmissionData()
        if (requestBody) {
            onSubmit(requestBody)
        }
    }

    const contactFields = getContactFields()
    const additionalFields = getAdditionalFields()
    const sortedCustomQuestions = [...(guestCard?.card_questions ?? [])].sort(
        (a, b) => a.order - b.order,
    )
    const hasCustomQuestions = sortedCustomQuestions.length > 0
    const hasAdditionalInfo = additionalFields.length > 0 || hasCustomQuestions

    return (
        <form
            onSubmit={handleSubmit}
            noValidate={Boolean(editor) || hideSubmit}
        >
            {/* Contact Information */}
            {contactFields.length > 0 && (
                <>
                    <Typography className={classes.sectionTitle}>
                        Contact Information
                    </Typography>
                    {contactFields.map((field) => {
                        const fieldName = field as string
                        const label = GUEST_CARD_FIELD_QUESTIONS[field]
                        const isReadOnly =
                            (fieldName === 'name' &&
                                !!guestCardFormData?.name) ||
                            (fieldName === 'email' &&
                                !!guestCardFormData?.email) ||
                            (fieldName === 'phone' &&
                                !!guestCardFormData?.phone)

                        if (fieldName === 'phone') {
                            return wrapDefault(
                                field,
                                <TextField
                                    className={classes.formField}
                                    fullWidth
                                    label={label}
                                    value={formData[fieldName] || ''}
                                    onChange={(e) => {
                                        const normalized = normalizePhone(
                                            e.target.value,
                                        )
                                        handleFieldChange(fieldName, normalized)
                                    }}
                                    variant="outlined"
                                    required={isFieldRequired(field)}
                                    type="tel"
                                    InputProps={{
                                        readOnly: isReadOnly,
                                    }}
                                    style={{
                                        backgroundColor: isReadOnly
                                            ? '#f9fafb'
                                            : 'transparent',
                                    }}
                                />,
                            )
                        }

                        return wrapDefault(
                            field,
                            <TextField
                                className={classes.formField}
                                fullWidth
                                label={label}
                                value={formData[fieldName] || ''}
                                onChange={(e) =>
                                    handleFieldChange(fieldName, e.target.value)
                                }
                                variant="outlined"
                                required={isFieldRequired(field)}
                                type={fieldName === 'email' ? 'email' : 'text'}
                                InputProps={{
                                    readOnly: isReadOnly,
                                }}
                                style={{
                                    backgroundColor: isReadOnly
                                        ? '#f9fafb'
                                        : 'transparent',
                                }}
                            />,
                        )
                    })}
                </>
            )}

            {/* Additional Information + custom questions */}
            {(hasAdditionalInfo || hasCustomQuestions) && (
                <>
                    <Typography className={classes.sectionTitle}>
                        Additional Information
                    </Typography>
                    {additionalFields
                        .filter((field) => field !== 'preferred_roommates')
                        .map((field) => {
                            const fieldName = field as string
                            const label = GUEST_CARD_FIELD_QUESTIONS[field]

                            if (fieldName === 'preferred_unit_config') {
                                return wrapDefault(
                                    field,
                                    <FormControl
                                        className={classes.formField}
                                        fullWidth
                                        variant="outlined"
                                        required={isFieldRequired(field)}
                                    >
                                        <InputLabel>{label}</InputLabel>
                                        <Select
                                            value={formData[fieldName] || ''}
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    fieldName,
                                                    e.target.value as string,
                                                )
                                            }
                                            label={label}
                                        >
                                            {guestCardFormData?.unit_configs.map(
                                                (config) => (
                                                    <MenuItem
                                                        key={config.id}
                                                        value={config.id.toString()}
                                                    >
                                                        {config.name}
                                                        {config.bed_count &&
                                                        config.bath_count
                                                            ? ` (${config.bed_count} bed, ${config.bath_count} bath)`
                                                            : ''}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </Select>
                                    </FormControl>,
                                )
                            }

                            if (fieldName === 'preferred_premiums') {
                                return wrapDefault(
                                    field,
                                    <GuestCardPremiumQuestion
                                        className={classes.formField}
                                        label={label}
                                        premiums={
                                            guestCardFormData?.premiums || []
                                        }
                                        selectedIds={selectedPremiumIds}
                                        noneSelected={premiumsNone}
                                        required={isFieldRequired(field)}
                                        onChange={({
                                            selectedIds,
                                            noneSelected,
                                        }) => {
                                            setSelectedPremiumIds(selectedIds)
                                            setPremiumsNone(noneSelected)
                                        }}
                                    />,
                                )
                            }

                            if (fieldName === 'school_attendance') {
                                const selectedSchool =
                                    schoolList?.find(
                                        (school) =>
                                            school.id.toString() ===
                                            formData[fieldName],
                                    ) || null

                                return wrapDefault(
                                    field,
                                    <Autocomplete<School, false, false, false>
                                        options={schoolList || []}
                                        getOptionLabel={(option: School) =>
                                            schoolDisplayName(option)
                                        }
                                        value={selectedSchool}
                                        onChange={(
                                            event: any,
                                            newValue: School | null,
                                        ) => {
                                            handleFieldChange(
                                                fieldName,
                                                newValue
                                                    ? newValue.id.toString()
                                                    : '',
                                            )
                                        }}
                                        loading={schoolListLoading}
                                        renderInput={(params: any) => (
                                            <TextField
                                                {...params}
                                                className={classes.formField}
                                                label={label}
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Search and select school..."
                                                required={isFieldRequired(
                                                    field,
                                                )}
                                            />
                                        )}
                                    />,
                                )
                            }

                            if (
                                fieldName === 'preferred_move_in_date' ||
                                fieldName === 'preferred_move_out_date'
                            ) {
                                return wrapDefault(
                                    field,
                                    <TextField
                                        className={classes.formField}
                                        fullWidth
                                        label={label}
                                        value={formData[fieldName] || ''}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                fieldName,
                                                e.target.value,
                                            )
                                        }
                                        variant="outlined"
                                        type="date"
                                        required={isFieldRequired(field)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />,
                                )
                            }

                            if (fieldName === 'preferred_rent_cost') {
                                return wrapDefault(
                                    field,
                                    <TextField
                                        className={classes.formField}
                                        fullWidth
                                        label={label}
                                        value={formData[fieldName] || ''}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                fieldName,
                                                e.target.value,
                                            )
                                        }
                                        variant="outlined"
                                        type="number"
                                        required={isFieldRequired(field)}
                                        inputProps={{
                                            min: 0,
                                            step: 1,
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    $
                                                </InputAdornment>
                                            ),
                                        }}
                                    />,
                                )
                            }

                            return wrapDefault(
                                field,
                                <TextField
                                    className={classes.formField}
                                    fullWidth
                                    label={label}
                                    value={formData[fieldName] || ''}
                                    onChange={(e) =>
                                        handleFieldChange(
                                            fieldName,
                                            e.target.value,
                                        )
                                    }
                                    variant="outlined"
                                    type="text"
                                    required={isFieldRequired(field)}
                                    multiline={
                                        fieldName === 'additional_requests'
                                    }
                                    rows={
                                        fieldName === 'additional_requests'
                                            ? 4
                                            : 1
                                    }
                                />,
                            )
                        })}

                    {/* Custom Questions */}
                    {hasCustomQuestions &&
                        guestCard.card_questions.map((cardQuestion) =>
                            wrapCustom(
                                cardQuestion.custom_question.id,
                                <TextField
                                    className={classes.formField}
                                    fullWidth
                                    label={
                                        cardQuestion.custom_question.question
                                    }
                                    value={
                                        formData[
                                            `custom_${cardQuestion.custom_question.id}`
                                        ] || ''
                                    }
                                    onChange={(e) =>
                                        handleFieldChange(
                                            `custom_${cardQuestion.custom_question.id}`,
                                            e.target.value,
                                        )
                                    }
                                    variant="outlined"
                                    required={cardQuestion.required}
                                />,
                            ),
                        )}

                    {/* Roommates Section - Always last */}
                    {additionalFields.includes('preferred_roommates') &&
                        wrapDefault(
                            'preferred_roommates',
                            <Box>
                                <FormControl
                                    component="fieldset"
                                    className={classes.formField}
                                    fullWidth
                                    required={isFieldRequired(
                                        'preferred_roommates',
                                    )}
                                >
                                    <FormLabel component="legend">
                                        {
                                            GUEST_CARD_FIELD_QUESTIONS.preferred_roommates
                                        }
                                    </FormLabel>
                                    <RadioGroup
                                        value={hasRoommates}
                                        onChange={(e) => {
                                            const value = e.target.value
                                            setHasRoommates(value)
                                            if (value === 'no') {
                                                setRoommates([])
                                            } else {
                                                if (roommates.length === 0) {
                                                    setRoommates([
                                                        {
                                                            name: '',
                                                            email: '',
                                                            phone: '',
                                                        },
                                                    ])
                                                }
                                            }
                                        }}
                                        row
                                    >
                                        <FormControlLabel
                                            value="no"
                                            control={<Radio />}
                                            label="No"
                                        />
                                        <FormControlLabel
                                            value="yes"
                                            control={<Radio />}
                                            label="Yes"
                                        />
                                    </RadioGroup>
                                </FormControl>

                                {hasRoommates === 'yes' && (
                                    <Box>
                                        {roommates.map((roommate, index) => (
                                            <Card
                                                key={index}
                                                style={{
                                                    marginBottom: theme.spacing(
                                                        1.5,
                                                    ),
                                                    padding: theme.spacing(2),
                                                    backgroundColor: '#f9fafb',
                                                }}
                                            >
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    marginBottom={1}
                                                >
                                                    <Typography variant="subtitle2">
                                                        Roommate {index + 1}
                                                    </Typography>
                                                    {roommates.length > 1 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                const updated = roommates.filter(
                                                                    (_, i) =>
                                                                        i !==
                                                                        index,
                                                                )
                                                                setRoommates(
                                                                    updated,
                                                                )
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                <TextField
                                                    className={
                                                        classes.formField
                                                    }
                                                    fullWidth
                                                    label="Name"
                                                    value={roommate.name}
                                                    onChange={(e) => {
                                                        const updated = [
                                                            ...roommates,
                                                        ]
                                                        updated[index].name =
                                                            e.target.value
                                                        setRoommates(updated)
                                                    }}
                                                    variant="outlined"
                                                    size="small"
                                                    style={{
                                                        marginBottom: theme.spacing(
                                                            1.5,
                                                        ),
                                                    }}
                                                />
                                                <TextField
                                                    className={
                                                        classes.formField
                                                    }
                                                    fullWidth
                                                    label="Email"
                                                    type="email"
                                                    value={roommate.email}
                                                    onChange={(e) => {
                                                        const updated = [
                                                            ...roommates,
                                                        ]
                                                        updated[index].email =
                                                            e.target.value
                                                        setRoommates(updated)
                                                    }}
                                                    variant="outlined"
                                                    size="small"
                                                    style={{
                                                        marginBottom: theme.spacing(
                                                            1.5,
                                                        ),
                                                    }}
                                                />
                                                <TextField
                                                    className={
                                                        classes.formField
                                                    }
                                                    fullWidth
                                                    label="Phone"
                                                    type="tel"
                                                    value={roommate.phone}
                                                    onChange={(e) => {
                                                        const normalized = normalizePhone(
                                                            e.target.value,
                                                        )
                                                        const updated = [
                                                            ...roommates,
                                                        ]
                                                        updated[
                                                            index
                                                        ].phone = normalized
                                                        setRoommates(updated)
                                                    }}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Card>
                                        ))}
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                                setRoommates([
                                                    ...roommates,
                                                    {
                                                        name: '',
                                                        email: '',
                                                        phone: '',
                                                    },
                                                ])
                                            }}
                                            style={{
                                                marginTop: theme.spacing(1),
                                                textTransform: 'none',
                                            }}
                                        >
                                            Add Another Roommate
                                        </Button>
                                    </Box>
                                )}
                            </Box>,
                        )}
                </>
            )}

            {!hideSubmit && (
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    className={classes.submitButton}
                    disabled={isLoading}
                >
                    {isLoading ? loadingLabel : submitLabel}
                </Button>
            )}
        </form>
    )
}
